// Mix & Match (bulk pricing) — port of Android `OrderCreateViewModel.validateBulkPrice`, including
// the QV-6974/6975 `max_qty` UNIT CAP (+ straddling-line split) and the QV-7124 dezi suppression.
//
// Per-product bulk pricing keyed by `model.bpId`. Once the eligible units of a bpId group reach
// `min_qty` (`model.bulkQuantity`), the group is bulk-priced — but only up to `max_qty` UNITS
// (`model.bulkMaxQty`); beyond the cap the units stay at full price.
//
// ── WHICH UNITS GET THE DISCOUNT ────────────────────────────────────────────────────────────────
// EARLIEST-ADDED (`orderId` ASC) — the owner-approved money decision (it was cheapest-first before
// 2026-07-13). Once the cap is reached the discount does NOT shift to a later-added cheaper unit. A
// split-off overflow line gets a fresh, LARGER orderId so it stays in the full-price tail, which is
// what makes re-derivation stable (an already-split pair fits exactly and never re-splits).
//
// ⚠️ Deliberately DIFFERENT from BOGO, which is latest-first (Design 2). Do not "unify" them.
//
// Reads bulk fields off line.model: bpId, bulkQuantity (min_qty), bulkPrice (discount), bulkMaxQty,
// bulkIsPercentage.

import { roundHalfUp, toNum } from "./money";
import { cappedMixMatchUnits, mixMatchDiscountAllocation } from "./promoHelpers";
import { isDeziLine } from "./dezi";

export function isBulkQtyApproved(model, totalQty, line) {
  const bq = model.bulkQuantity;
  if (bq == null || bq === "") return false;
  return totalQty >= toNum(bq) && toNum(line.orderDiscount) === 0 && toNum(line.orderDiscountPercent) === 0;
}

function isManualOverride(line) {
  return (
    !line.isBulkPriceApplied &&
    toNum(line.orderUnitNewPrice) > 0 &&
    toNum(line.orderUnitPrice) > 0 &&
    Math.abs(toNum(line.orderUnitNewPrice) - toNum(line.orderUnitPrice)) > 0.001
  );
}

export function calculateBulkPrice(line) {
  const m = line.model || {};
  // Subtract the bulk discount from the LINE's list price (catalogUnitPrice = the VARIANT price for a
  // variant, base for non-variant) — NOT m.price (the base product price). For a variant where base ≠
  // variant price, using m.price computed the discount off the wrong base. catalogUnitPrice keeps
  // orderUnitNewPrice consistent with orderUnitPrice, so mix_match_amt = (newPrice − orderUnitPrice)
  // = −bulkPrice, matching Android.
  const price = line.catalogUnitPrice != null ? toNum(line.catalogUnitPrice) : toNum(m.price);
  line.isBulkPriceApplied = true;
  const isPct = m.bulkIsPercentage === "1" || m.bulkIsPercentage === 1 || m.bulkIsPercentage === true;
  if (isPct) {
    let pct = toNum(m.bulkPrice);
    if (pct < 0) pct = 0;
    if (pct > 99.99) pct = 99.99;
    let np = roundHalfUp(price - (price * pct) / 100);
    if (np <= 0) np = 0.01; // floor at a cent (never free)
    line.orderUnitNewPrice = np;
  } else {
    // flat: bulkPrice is the discount amount off catalog
    line.orderUnitNewPrice = toNum(m.bulkPrice) < price ? roundHalfUp(price - toNum(m.bulkPrice)) : 0;
  }
}

export function restoreBulkPrice(line) {
  line.isBulkPriceApplied = false;
  line.orderUnitNewPrice = toNum(line.manualOverridePrice);
}

/**
 * Re-derive Mix & Match across the cart.
 *
 * @param cart        cart lines (mutated in place; a straddling line may be SPLIT, so the array can grow)
 * @param deziUpcSet  Set of dezi upcs — those lines never take the bulk price (QV-7124 rule 1) and are
 *                    excluded from the qty count, so a dezi line can't even help a group qualify
 * @param nextLineId  () => number, used to give a split-off overflow line a fresh LARGER orderId
 */
export function validateBulkPrice(cart, deziUpcSet = null, nextLineId = null) {
  const groups = new Map();
  for (const l of cart) {
    const bp = (l.model || {}).bpId;
    if (bp == null || bp === "") continue;
    if (!groups.has(bp)) groups.set(bp, []);
    groups.get(bp).push(l);
  }

  for (const lines of groups.values()) {
    // dezi WINS: restore any dezi line this group already bulk-priced, then keep dezi out of the
    // eligible set entirely. A bpId group can span several upcs, so only the dezi ones are dropped —
    // non-dezi products in the same deal keep Mix & Match.
    for (const l of lines) if (isDeziLine(l, deziUpcSet) && l.isBulkPriceApplied) restoreBulkPrice(l);

    const eligible = lines.filter(
      (l) =>
        !isManualOverride(l) &&
        toNum(l.orderDiscount) === 0 &&
        toNum(l.orderDiscountPercent) === 0 &&
        !isDeziLine(l, deziUpcSet)
    );
    const totalQty = eligible.reduce((s, l) => s + toNum(l.orderCount), 0);
    const model = (eligible[0] || {}).model || {};

    if (!eligible.length || !isBulkQtyApproved(model, totalQty, eligible[0])) {
      for (const l of lines) if (l.isBulkPriceApplied) restoreBulkPrice(l);
      continue;
    }

    // max_qty UNIT CAP. null / blank / 0 / negative = unlimited (discount every eligible unit).
    const unitsToDiscount = cappedMixMatchUnits(totalQty, model.bulkMaxQty);
    if (unitsToDiscount >= totalQty) {
      for (const l of eligible) calculateBulkPrice(l);
      continue;
    }

    // cap < totalQty → grant the discount to the EARLIEST-ADDED `unitsToDiscount` units.
    const sorted = [...eligible].sort((a, b) => toNum(a.orderId) - toNum(b.orderId));
    const allocation = mixMatchDiscountAllocation(
      sorted.map((l) => toNum(l.orderCount)),
      unitsToDiscount
    );
    sorted.forEach((line, i) => {
      const give = allocation[i];
      const count = toNum(line.orderCount);
      if (give <= 0) {
        restoreBulkPrice(line);
      } else if (give >= count) {
        calculateBulkPrice(line);
      } else {
        // STRADDLE → split into a discounted chunk (`give` units) + a full-price overflow chunk.
        // In practice every register line is 1 unit (adds explode into per-unit rows), so this is a
        // defensive safety net — but a restored hold can carry orderCount > 1.
        const overflow = count - give;
        const idx = cart.indexOf(line);
        const fullChunk = {
          ...JSON.parse(JSON.stringify(line)),
          orderId: nextLineId ? nextLineId() : toNum(line.orderId) + 0.5,
          orderCount: overflow,
        };
        restoreBulkPrice(fullChunk);
        line.orderCount = give;
        calculateBulkPrice(line);
        if (idx >= 0) cart.splice(idx + 1, 0, fullChunk);
        else cart.push(fullChunk);
      }
    });
  }
  return cart;
}
